import React, { useState, useRef } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Send, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { BRAND } from "../data/site";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", website: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const mountedAt = useRef(Date.now());
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await api.post("/contacts", { ...form, elapsed_ms: Date.now() - mountedAt.current });
      setSent(true);
      toast.success("Message sent! We'll get back to you shortly.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full px-4 py-3 rounded-xl border border-[#D6E4DC] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-lime/50 focus:border-brand-lime";
  const labelCls = "block text-xs font-bold text-brand-emerald uppercase tracking-wider mb-1.5";

  return (
    <div className="pt-24 lg:pt-32 pb-20 bg-brand-canvas min-h-screen" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-12">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-brand-lime mb-3">Contact Us</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-brand-emerald tracking-tight">Let's talk about your space</h1>
          <p className="mt-4 text-brand-muted text-base sm:text-lg">Questions, quotes, custom contracts — send us a message or call directly. We reply fast.</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2 space-y-4">
            {[
              { icon: Phone, label: "Call or WhatsApp", value: BRAND.phone, href: BRAND.phoneHref, tid: "contact-phone" },
              { icon: Mail, label: "Email Us", value: BRAND.email, href: `mailto:${BRAND.email}`, tid: "contact-email" },
              { icon: MapPin, label: "Coverage", value: "All Lagos — Island & Mainland", tid: "contact-location" },
              { icon: Clock, label: "Working Hours", value: BRAND.hours, tid: "contact-hours" },
            ].map((c) => (
              <div key={c.label} className="bg-white rounded-2xl border border-[#D6E4DC] p-6 flex items-start gap-4 hover:border-brand-lime/50 hover:shadow-md transition-all" data-testid={c.tid}>
                <div className="w-11 h-11 rounded-xl bg-brand-emerald flex items-center justify-center shrink-0">
                  <c.icon className="w-5 h-5 text-brand-lime" />
                </div>
                <div>
                  <p className="text-xs font-bold text-brand-muted uppercase tracking-wider">{c.label}</p>
                  {c.href ? (
                    <a href={c.href} className="font-semibold text-brand-emerald hover:text-brand-lime transition-colors break-all">{c.value}</a>
                  ) : (
                    <p className="font-semibold text-brand-emerald">{c.value}</p>
                  )}
                </div>
              </div>
            ))}
            <div className="hero-gradient rounded-2xl p-6 text-white">
              <p className="font-display italic text-brand-gold text-lg">{BRAND.promise}</p>
              <p className="text-sm text-white/70 mt-2">Every enquiry is read by a real member of our team — no bots, no call centres.</p>
            </div>
          </div>

          <div className="lg:col-span-3">
            {sent ? (
              <div className="bg-white rounded-3xl border border-[#D6E4DC] p-12 text-center h-full flex flex-col items-center justify-center" data-testid="contact-success">
                <CheckCircle2 className="w-16 h-16 text-brand-lime mb-5" />
                <h2 className="font-display text-2xl font-bold text-brand-emerald">Message received!</h2>
                <p className="mt-3 text-brand-muted max-w-sm">Thank you, {form.name.split(" ")[0]}. We'll reply to <strong>{form.email}</strong> shortly — usually within a few hours.</p>
                <button onClick={() => { setSent(false); setForm({ name: "", email: "", phone: "", subject: "", message: "", website: "" }); }} data-testid="contact-send-another" className="mt-6 px-6 py-3 rounded-full bg-brand-emerald text-white font-bold text-sm hover:bg-brand-lime transition-colors">Send another message</button>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white rounded-3xl border border-[#D6E4DC] p-6 sm:p-10 shadow-lg shadow-brand-emerald/5" data-testid="contact-form">
                <input type="text" name="website" value={form.website} onChange={(e) => set("website", e.target.value)} className="hp-field" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelCls}>Full Name *</label>
                    <input data-testid="contact-name-input" required minLength={2} value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} placeholder="Your name" />
                  </div>
                  <div>
                    <label className={labelCls}>Email *</label>
                    <input data-testid="contact-email-input" required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} placeholder="you@example.com" />
                  </div>
                  <div>
                    <label className={labelCls}>Phone (optional)</label>
                    <input data-testid="contact-phone-input" value={form.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} placeholder="080x xxx xxxx" />
                  </div>
                  <div>
                    <label className={labelCls}>Subject *</label>
                    <input data-testid="contact-subject-input" required minLength={2} value={form.subject} onChange={(e) => set("subject", e.target.value)} className={inputCls} placeholder="e.g. Office cleaning quote" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelCls}>Message *</label>
                    <textarea data-testid="contact-message-input" required minLength={5} rows={6} value={form.message} onChange={(e) => set("message", e.target.value)} className={inputCls} placeholder="Tell us about your space and what you need…" />
                  </div>
                </div>
                <p className="text-xs text-brand-muted mt-4">Protected by anti-spam verification. We never share your details.</p>
                <button data-testid="contact-submit-btn" disabled={sending} className="mt-6 px-8 py-3.5 rounded-full bg-brand-emerald text-white font-bold hover:bg-brand-lime disabled:opacity-50 transition-colors flex items-center gap-2">
                  <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send Message"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
